import { Sidebar } from "src/components/Sidebar/Sidebar"
import { ProjectImage } from "src/components/ProjectImage/ProjectImage"
import "./styles.scss"

const MainContent = () => {
    return (
        <div className="main-content p-4">
            <div className="header mb-4">
                <h1 className="title">OSW-2400</h1>
                <h2 className="subtitle">Full Stack Embedded Web Application — SAS switch Management Interface</h2>
            </div>

            <div className="description mb-5">
                <p>
                    Working on the OSW-2400 Switch was a big shift for me. My background up to this point had been in consumer-facing SaaS, mostly at startups where speed and product iteration were the priority. This project put me in a completely different world — enterprise hardware, embedded systems, and a large organization with engineers, product managers, and stakeholders across multiple teams.
                </p>
                <p>
                    The product is a browser-based management interface for a SAS switch, which is the hardware responsible for routing data between tape library drives and host bus adapters. It runs on a Raspberry Pi Compute Module 5 and communicates with the switch over a serial UART connection. The goal was to replace what would otherwise require direct serial CLI access with a secure, role-controlled web UI that data center operations teams could use without needing hardware expertise.
                </p>
                <p>
                    I came onto the project working alongside two senior engineers who helped establish the initial architecture and get the first releases out the door. After that, I took over the entire codebase — frontend, backend, and testing infrastructure — and continued development from there on my own. A big part of what made this project possible for me was how I work with AI. Go was new to me, embedded hardware was new to me, and the domain — SAS storage infrastructure — was completely foreign. But I was able to ramp up quickly and stay productive by leaning into test driven AI-assisted development in a way that let me move past the learning curve without slowing the team down.
                </p>
            </div>

            <div className="project-screenshot">
                <ProjectImage
                    src="/assets/Screenshots/OSWdashboard.png"
                    alt="OSW-2400 Dashboard"
                    description="The main dashboard — SAS port status, active zones, temperatures, fan speeds, and power supply state at a glance"
                />
            </div>

            <div className="description things-i-worked-on mb-5">
                <h4>Things I Worked On</h4>
                <p>My main areas of ownership across the project were:</p>
                <ul>
                    <li><a href="#frontend" className="work-link">Frontend & UX</a></li>
                    <li><a href="#backend" className="work-link">Go Server Architecture</a></li>
                    <li><a href="#testing" className="work-link">Testing Infrastructure</a></li>
                </ul>
            </div>

            <div id="frontend" className="description mb-5">
                <h4>Frontend & UX</h4>
                <p>
                    I owned the entire frontend codebase, building out the full component library and all the major views — PHY port status, topology diagram, zoning configuration, environment metrics charts, firmware update flow, and user management screens.
                </p>
                <p>
                    A big part of the challenge was making complex hardware state readable to operations teams who are not hardware experts. That meant working closely with stakeholders, incorporating feedback from sales and customer success, and building within the design language already established across the company's other products rather than starting from scratch. Getting the interface right for the audience mattered as much as getting the data right.
                </p>
                <ProjectImage
                    src="/assets/Screenshots/OSWtopology.png"
                    alt="OSW-2400 Topology view"
                    description="Topology view — visual map of the switch's upstream and downstream device connections at the PHY level"
                    width="90%"
                />
                <ProjectImage
                    src="/assets/Screenshots/OSWzoning.png"
                    alt="OSW-2400 Zoning configuration"
                    description="Zoning configuration — assign lanes to zones, track pending vs active state, and apply changes to hardware"
                    width="90%"
                />
                <ProjectImage
                    src="/assets/Screenshots/OSWtemps.png"
                    alt="OSW-2400 Environment metrics"
                    description="Environment monitoring — historical temperature charts with configurable time ranges and aggregation intervals"
                    width="90%"
                />
                <ul>
                    <li>React 18 / TypeScript with Material UI</li>
                    <li>WebSocket integration for real-time PHY and sensor data pushed from the server</li>
                    <li>Route-level auth guards with role-based view access (Operator, Admin, Super User)</li>
                    <li>Topology diagram showing upstream/downstream device relationships with PHY-level WWN aliases</li>
                    <li>Interactive time-range charts for historical sensor and performance metrics</li>
                    <li>Full settings section including network config, NTP, TLS, SSH, and syslog</li>
                </ul>
            </div>

            <div id="backend" className="description mb-5">
                <h4>Go Server Architecture</h4>
                <p>
                    The server is written in Go, which was new territory for me coming from Python and TypeScript. I designed and implemented the HTTP server structure — the REST API layout, WebSocket hub, middleware stack for auth, logging and CORS, and the background polling system that continuously reads hardware data from the switch over serial.
                </p>
                <p>
                    One of the more interesting parts of the backend was the OpenAPI-driven type system, which kept the API contract between the server and frontend explicit. I also worked extensively on the JWT authentication system and the OTA firmware update flow, both of which were established by the senior engineers early on — my work involved extending and adding to them as new requirements came in. The licensing system, which enforces drive OUI whitelists by disabling unlicensed drives at the PHY level, was another area I contributed to, including support for offline activation in air-gapped environments.
                </p>
                <ul>
                    <li>Go HTTP server with REST API and WebSocket hub</li>
                    <li>JWT authentication with role-based enforcement</li>
                    <li>Background polling system for real-time hardware data collection over serial UART</li>
                    <li>OTA firmware updates with A/B partitions and automatic rollback</li>
                    <li>Licensing and entitlement enforcement at the PHY hardware level</li>
                    <li>OpenAPI-driven type system for API contract enforcement</li>
                </ul>
            </div>

            <div id="testing" className="description mb-5">
                <h4>Testing Infrastructure</h4>
                <p>
                    Testing on this project was something I took seriously from early on, and for good reason. The hardware communicates over a serial UART connection, which means you can not just spin up the app locally and poke at real devices. If the only way to verify that the serial communication layer works correctly is to have physical hardware in front of you, you have a big problem — it makes the feedback loop slow, it makes CI impossible, and it means bugs that should be caught early only surface in integration.
                </p>
                <p>
                    To solve this, I built a CLI simulator: a file-based serial port mock that replays realistic switch responses with CRC verification. This let the full hardware communication layer run in tests without any physical hardware at all. It became the foundation for the integration test suite I wrote for the PHY manager, authentication system, CLI command parsing, and zoning logic.
                </p>
                <p>
                    I also established the project-wide testing patterns, including a <code>th</code> test helper package that became the standard in place of external assertion libraries. Getting this infrastructure in place early paid off throughout the project — it meant that changes to the serial communication layer or zone management could be validated quickly and with confidence, which matters a lot when the thing you're shipping runs on embedded hardware in customer data centers.
                </p>
                <ul>
                    <li>File-based serial port mock (CLI simulator) with CRC verification for hardware-free testing</li>
                    <li>Integration test suite covering PHY manager, auth, CLI parsing, and zoning logic</li>
                    <li>Internal <code>th</code> test helper package as project-wide testing standard</li>
                </ul>
            </div>

            <div id="conclusion" className="description mb-5">
                <h4>Takeaways</h4>
                <p>
                    This project was a big growth moment for me. A new language, a totally different domain, and a larger organization than I was used to. After the initial releases shipped and I took over the codebase, I continued working on test architecture, bug fixes, and new features, and spent time helping support team members get up to speed on the system. It was a good reminder that there is a lot more to shipping software than just the code.
                </p>
            </div>

        </div>
    )
}

interface ProjectLayoutProps {
    sidebar: React.ReactNode;
    children: React.ReactNode;
}

const ProjectLayout: React.FC<ProjectLayoutProps> = ({ sidebar, children }) => (
    <div className="container-fluid p-0">
        <div className="row g-0">
            <div className="col-md-3">{sidebar}</div>
            <div className="col-md-9">{children}</div>
        </div>
    </div>
)

export default function OswProject() {
    const sidebarProps = {
        toolUrl: "",
        disabledLink: true,
        responsibilities: [
            "React / TypeScript",
            "Material UI",
            "Go",
            "WebSockets",
            "JWT Auth",
            "Serial UART",
            "OTA Firmware Updates",
            "Integration Testing",
            "Embedded Linux (Raspberry Pi CM5)"
        ]
    }

    return (
        <ProjectLayout sidebar={<Sidebar {...sidebarProps} />}>
            <MainContent />
        </ProjectLayout>
    )
}
